import { useCallback, useContext, useMemo } from 'react';

import { TokenContext } from '@/Contexts/TokenContext';
import { useCustomFetch } from '@/hooks/useCustomFetch';
import { CopyApplicationCyclesRequest } from '@/models/Admin/CopyApplicationCycles/CopyApplicationCyclesRequest';
import { CopyApplicationCyclesResponse } from '@/models/Admin/CopyApplicationCycles/CopyApplicationCyclesResponse';
import { baseUrl } from '@/utils/constants';
import { UpdateApplicationCyclesRequest } from '@/models/Admin/UpdateApplicationCycle/UpdateApplicationCyclesRequest';
import { ApplicationCycle } from '@/models/ApplicationCycle';

export default function useAdminApplicationCycle() {
  const adminAppCycleUrl = useMemo(() => {
    return `${baseUrl}admin/applicationCycles`;
  }, []);

  const { authResult } = useContext(TokenContext);
  const { customFetch } = useCustomFetch();

  const copyApplicationCycles = useCallback(
    async (fromYear: number, toYear: number) => {
      const fetchUrl = `${adminAppCycleUrl}/copy`;

      const requestBody = {
        YearToCopyFrom: fromYear,
        YearToCopyTo: toYear,
      } satisfies CopyApplicationCyclesRequest;

      // setup a timeout for this request since it may take a while
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 seconds timeout

      const response = await customFetch<CopyApplicationCyclesResponse>(fetchUrl, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      return response;
    },
    [customFetch, authResult, adminAppCycleUrl]
  );

  const validateApplicationCycleDates = useCallback(
    (openDate: Date, closeDate: Date): string | null => {
      // make sure opena and close dates are actually dates
      if (!(openDate instanceof Date) || isNaN(openDate.getTime())) {
        return 'Invalid open date.';
      }

      if (!(closeDate instanceof Date) || isNaN(closeDate.getTime())) {
        return 'Invalid close date.';
      }

      if (openDate >= closeDate) {
        return 'Open date must be before close date.';
      }

      return null;
    },
    []
  );

  const updateApplicationCycle = useCallback(
    async (appCycleId: number, openDate: Date, closeDate: Date): Promise<ApplicationCycle> => {
      const fetchUrl = `${adminAppCycleUrl}/${appCycleId}`;

      const validationError = validateApplicationCycleDates(openDate, closeDate);
      if (validationError) {
        throw new Error(validationError);
      }

      const requestBody = {
        OpenDate: openDate,
        CloseDate: closeDate,
      } satisfies UpdateApplicationCyclesRequest;

      const response = await customFetch<ApplicationCycle>(fetchUrl, {
        method: 'PUT',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      return response;
    },
    [customFetch, authResult, adminAppCycleUrl, validateApplicationCycleDates]
  );

  return { copyApplicationCycles, updateApplicationCycle };
}
